import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, type Observable } from 'rxjs';
import { unwrapApiResponse } from '@shared/api';
import { environment } from 'src/environments/environment';
import type {
  Aplicacion,
  AplicacionInput,
  Funcion,
  FuncionInput,
  Modulo,
  ModuloInput,
  Rol,
  RolInput,
} from '@schemas/accesos';
import type { CambioPrevisto, OperacionPrevisible } from '@schemas/cambios';
import { AccesosService } from './accesos.service';

/**
 * Gestor de accesos contra `AdminPortal.API` (esquema `acceso` de la base
 * propia).
 *
 * Sustituyó a `AccesosMockService`, que persistía en `localStorage` y ya se
 * borró. Las
 * pantallas no cambian: las dos implementan `AccesosService`, y por eso el
 * cambio es una línea en `app.config.ts`.
 *
 * ── Por qué esta clase es casi solo llamadas ─────────────────────────────────
 *
 * Las reglas de negocio no están aquí y no deben estar: viven en la API, que es
 * el único lugar donde no se pueden esquivar. El mock las implementaba porque
 * era el único que había; replicarlas ahora dejaría dos copias de «no puedes dar
 * de baja una aplicación con roles enlazados» que divergirían a la primera
 * corrección.
 *
 * Lo que sí hay aquí es la traducción del contrato de la pantalla al de la API,
 * y está señalada caso por caso.
 */
@Injectable({ providedIn: 'root' })
export class AccesosHttpService extends AccesosService {
  private readonly http = inject(HttpClient);

  /**
   * Base RELATIVA: `environment.apiUrl` va vacío a propósito.
   *
   * Un host absoluto convertiría la cookie de sesión en una cookie de terceros,
   * que los navegadores bloquean cada vez más. En desarrollo el mismo origen lo
   * simula `proxy.conf.json` (`/api` → `localhost:9503`).
   */
  private readonly base = `${environment.apiUrl}/api/v1`;

  // ── Aplicaciones ───────────────────────────────────────────────────────────

  listarAplicaciones(): Observable<readonly Aplicacion[]> {
    return this.http
      .get<unknown>(`${this.base}/aplicaciones`)
      .pipe(unwrapApiResponse<readonly Aplicacion[]>());
  }

  crearAplicacion(input: AplicacionInput, motivo: string): Observable<Aplicacion> {
    return this.http
      .post<unknown>(`${this.base}/aplicaciones`, cuerpoAplicacion(input))
      .pipe(unwrapApiResponse<Aplicacion>());
  }

  actualizarAplicacion(id: number, input: AplicacionInput, motivo: string): Observable<Aplicacion> {
    return this.http
      .put<unknown>(`${this.base}/aplicaciones/${id}`, cuerpoAplicacion(input))
      .pipe(unwrapApiResponse<Aplicacion>());
  }

  /**
   * El contrato de la pantalla devuelve `void` y la API devuelve la fila ya
   * dada de baja. Se descarta a propósito: la pantalla recarga el listado
   * después, porque la baja de una aplicación cambia también los conteos de
   * las otras filas, y devolver solo una invitaría a pintarla sin recargar.
   */
  desactivarAplicacion(id: number, motivo: string): Observable<void> {
    return this.http.delete<unknown>(`${this.base}/aplicaciones/${id}`).pipe(
      unwrapApiResponse<unknown>(),
      map(() => undefined),
    );
  }

  reactivarAplicacion(id: number, motivo: string): Observable<Aplicacion> {
    return this.http
      .patch<unknown>(`${this.base}/aplicaciones/${id}/reactivar`, {})
      .pipe(unwrapApiResponse<Aplicacion>());
  }

  // ── Módulos ────────────────────────────────────────────────────────────────

  listarModulos(aplicacionId: number): Observable<readonly Modulo[]> {
    return this.http
      .get<unknown>(`${this.base}/modulos`, {
        params: new HttpParams().set('aplicacionId', aplicacionId),
      })
      .pipe(unwrapApiResponse<readonly Modulo[]>());
  }

  crearModulo(input: ModuloInput, motivo: string): Observable<Modulo> {
    // `orden` NO viaja: lo calcula el servidor (máximo + 10) y mandarlo se
    // rechaza. Ver `moverModulo` para el reordenamiento.
    return this.http
      .post<unknown>(`${this.base}/modulos`, {
        aplicacionId: input.aplicacionId,
        nombre: input.nombre,
        descripcion: input.descripcion ?? null,
        icono: input.icono ?? null,
        ruta: input.ruta,
        activo: input.activo ?? null,
      })
      .pipe(unwrapApiResponse<Modulo>());
  }

  /**
   * Edición PARCIAL: solo se manda lo que trae `input`.
   *
   * Se construye el cuerpo campo por campo en vez de reenviar el objeto porque
   * `undefined` desaparece al serializar pero `null` no, y en la API `null`
   * significa «no lo toques» para todo menos la descripción, donde la cadena
   * vacía es la que borra. Reenviar el objeto tal cual mandaría `null` en los
   * campos ausentes, que es lo mismo — pero por accidente, no por decisión.
   */
  actualizarModulo(id: number, input: Partial<ModuloInput>, motivo: string): Observable<Modulo> {
    const cuerpo: Record<string, unknown> = {};
    if (input.nombre !== undefined) cuerpo['nombre'] = input.nombre;
    if (input.descripcion !== undefined) cuerpo['descripcion'] = input.descripcion ?? '';
    if (input.icono !== undefined) cuerpo['icono'] = input.icono;
    if (input.ruta !== undefined) cuerpo['ruta'] = input.ruta;
    if (input.activo !== undefined) cuerpo['activo'] = input.activo;

    return this.http
      .put<unknown>(`${this.base}/modulos/${id}`, cuerpo)
      .pipe(unwrapApiResponse<Modulo>());
  }

  moverModulo(
    id: number,
    direccion: 'arriba' | 'abajo',
    motivo: string,
  ): Observable<readonly Modulo[]> {
    return this.http
      .patch<unknown>(`${this.base}/modulos/${id}/mover`, { direccion })
      .pipe(unwrapApiResponse<readonly Modulo[]>());
  }

  desactivarModulo(id: number, motivo: string): Observable<void> {
    return this.http.delete<unknown>(`${this.base}/modulos/${id}`).pipe(
      unwrapApiResponse<unknown>(),
      map(() => undefined),
    );
  }

  reactivarModulo(id: number, motivo: string): Observable<Modulo> {
    return this.http
      .patch<unknown>(`${this.base}/modulos/${id}/reactivar`, {})
      .pipe(unwrapApiResponse<Modulo>());
  }

  // ── Roles ──────────────────────────────────────────────────────────────────

  listarRoles(): Observable<readonly Rol[]> {
    return this.http.get<unknown>(`${this.base}/roles`).pipe(unwrapApiResponse<readonly Rol[]>());
  }

  crearRol(input: RolInput, motivo: string): Observable<Rol> {
    return this.http
      .post<unknown>(`${this.base}/roles`, cuerpoRol(input))
      .pipe(unwrapApiResponse<Rol>());
  }

  actualizarRol(id: number, input: RolInput, motivo: string): Observable<Rol> {
    return this.http
      .put<unknown>(`${this.base}/roles/${id}`, cuerpoRol(input))
      .pipe(unwrapApiResponse<Rol>());
  }

  desactivarRol(id: number, motivo: string): Observable<void> {
    return this.http.delete<unknown>(`${this.base}/roles/${id}`).pipe(
      unwrapApiResponse<unknown>(),
      map(() => undefined),
    );
  }

  reactivarRol(id: number, motivo: string): Observable<Rol> {
    return this.http
      .patch<unknown>(`${this.base}/roles/${id}/reactivar`, {})
      .pipe(unwrapApiResponse<Rol>());
  }

  // ── Funciones ──────────────────────────────────────────────────────────────

  listarFunciones(aplicacionId: number): Observable<readonly Funcion[]> {
    return this.http
      .get<unknown>(`${this.base}/funciones`, {
        params: new HttpParams().set('aplicacionId', aplicacionId),
      })
      .pipe(unwrapApiResponse<readonly Funcion[]>());
  }

  listarFuncionesPorModulo(moduloId: number): Observable<readonly Funcion[]> {
    return this.http
      .get<unknown>(`${this.base}/funciones`, {
        params: new HttpParams().set('moduloId', moduloId),
      })
      .pipe(unwrapApiResponse<readonly Funcion[]>());
  }

  crearFuncion(input: FuncionInput, motivo: string): Observable<Funcion> {
    return this.http
      .post<unknown>(`${this.base}/funciones`, {
        moduloId: input.moduloId,
        nombre: input.nombre,
        descripcion: input.descripcion ?? null,
        padreCodigo: input.padreCodigo ?? null,
        activo: input.activo ?? null,
      })
      .pipe(unwrapApiResponse<Funcion>());
  }

  /** Ver la nota de `actualizarModulo` sobre por qué el cuerpo se arma campo por campo. */
  actualizarFuncion(id: number, input: Partial<FuncionInput>, motivo: string): Observable<Funcion> {
    const cuerpo: Record<string, unknown> = {};
    if (input.nombre !== undefined) cuerpo['nombre'] = input.nombre;
    if (input.descripcion !== undefined) cuerpo['descripcion'] = input.descripcion ?? '';
    if (input.activo !== undefined) cuerpo['activo'] = input.activo;

    return this.http
      .put<unknown>(`${this.base}/funciones/${id}`, cuerpo)
      .pipe(unwrapApiResponse<Funcion>());
  }

  desactivarFuncion(id: number, motivo: string): Observable<readonly Funcion[]> {
    return this.http
      .delete<unknown>(`${this.base}/funciones/${id}`)
      .pipe(unwrapApiResponse<readonly Funcion[]>());
  }

  reactivarFuncion(id: number, motivo: string): Observable<readonly Funcion[]> {
    return this.http
      .patch<unknown>(`${this.base}/funciones/${id}/reactivar`, {})
      .pipe(unwrapApiResponse<readonly Funcion[]>());
  }

  // ── Vista previa ──────────────────────────────────────────────────────
  //
  // Ensaya la operación en el SERVIDOR y devuelve lo que cambiaría. No es un
  // diff: ejecuta la escritura de verdad dentro de una transacción, lee lo que
  // el disparador de la bitácora registró y hace ROLLBACK. Por eso enseña lo que
  // ningún diff local puede ver —lo derivado, las cascadas y las validaciones—.
  //
  // ⚠️ Medido: editar un rol sin mandar `modulosCodigos` BORRA sus otorgamientos
  // de módulo. El cuerpo es un reemplazo completo y el ensayo lo enseña como
  // siete DELETE; desde la pantalla no había forma de verlo venir.

  previsualizarAplicacion(
    operacion: OperacionPrevisible,
    id?: number,
    datos?: AplicacionInput,
  ): Observable<readonly CambioPrevisto[]> {
    return this.http
      .post<unknown>(`${this.base}/aplicaciones/vista-previa`, {
        operacion,
        id,
        alta: datos,
        edicion: datos,
      })
      .pipe(unwrapApiResponse<readonly CambioPrevisto[]>());
  }

  previsualizarModulo(
    operacion: OperacionPrevisible,
    id?: number,
    datos?: Partial<ModuloInput>,
  ): Observable<readonly CambioPrevisto[]> {
    return this.http
      .post<unknown>(`${this.base}/modulos/vista-previa`, {
        operacion,
        id,
        alta: datos,
        edicion: datos,
      })
      .pipe(unwrapApiResponse<readonly CambioPrevisto[]>());
  }

  previsualizarRol(
    operacion: OperacionPrevisible,
    id?: number,
    datos?: RolInput,
  ): Observable<readonly CambioPrevisto[]> {
    return this.http
      .post<unknown>(`${this.base}/roles/vista-previa`, {
        operacion,
        id,
        alta: datos,
        edicion: datos,
      })
      .pipe(unwrapApiResponse<readonly CambioPrevisto[]>());
  }

  previsualizarFuncion(
    operacion: OperacionPrevisible,
    id?: number,
    datos?: Partial<FuncionInput>,
  ): Observable<readonly CambioPrevisto[]> {
    return this.http
      .post<unknown>(`${this.base}/funciones/vista-previa`, {
        operacion,
        id,
        alta: datos,
        edicion: datos,
      })
      .pipe(unwrapApiResponse<readonly CambioPrevisto[]>());
  }
}

/**
 * Cuerpo del alta y la edición de una aplicación.
 *
 * El `codigo` no viaja aunque `AplicacionInput` lo declare: en el alta lo deriva
 * el servidor del nombre y en la edición está congelado. Mandarlo daría la
 * ilusión de que se puede elegir.
 */
function cuerpoAplicacion(input: AplicacionInput): Record<string, unknown> {
  return {
    nombre: input.nombre,
    descripcion: input.descripcion ?? null,
    icono: input.icono ?? null,
    url: input.url,
    activo: input.activo ?? null,
  };
}

function cuerpoRol(input: RolInput): Record<string, unknown> {
  return {
    nombre: input.nombre,
    descripcion: input.descripcion ?? null,
    icono: input.icono ?? null,
    aplicacionId: input.aplicacionId ?? null,
    esSuperadmin: input.esSuperadmin ?? null,
    // La `ruta` solo la usa el superadministrador; para el resto el servidor la
    // deriva de la URL de su aplicación y lo que se mande aquí se ignora.
    ruta: input.ruta ?? null,
    activo: input.activo ?? null,
    // Reemplazo COMPLETO. `null` y `[]` significan lo mismo —quitarlos todos—
    // porque el contrato de la pantalla manda siempre la lista entera.
    modulosCodigos: input.modulosCodigos ?? [],
    funcionesCodigos: input.funcionesCodigos ?? [],
  };
}
